<script setup lang="ts">
/**
 * StepImage — 安装步骤配图组件
 *
 * 约定目录（用户后续自己加图）：
 *   public/icons/install/{page}/{section}/step-N.webp
 *   例：/icons/install/manual/desktop/step-1.webp
 *       /icons/install/store/chrome/desktop/step-3.webp
 *
 * 行为：
 *   - 组件按规则生成 URL
 *   - <img> 加载成功 → 渲染图片
 *   - <img> 加载失败（404）→ silent 隐藏，不显示占位符
 *   - 不区分多语言：图片是 UI 元素
 *   - WebP 格式（用户在 public/ 目录下存放 .webp 文件）
 */
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    /** 路径前缀，例：'/icons/install/manual/desktop' */
    basePath: string
    /** 步骤序号（从 1 开始） */
    step: number
    /** 替代文本（来自 i18n step 文字） */
    alt: string
  }>(),
  {}
)

const url = computed(() => `${props.basePath}/step-${props.step}.webp`)
const loaded = ref(false)
const errored = ref(false)

function onLoad() {
  loaded.value = true
  errored.value = false
}

function onError() {
  loaded.value = false
  errored.value = true
}
</script>

<template>
  <figure
    v-if="!errored"
    class="step-image"
    :class="{ 'step-image--loaded': loaded }"
  >
    <img
      :src="url"
      :alt="alt"
      class="step-image__img"
      loading="lazy"
      decoding="async"
      @load="onLoad"
      @error="onError"
    />
  </figure>
</template>

<style scoped>
.step-image {
  display: none;
  margin: var(--yfb-sp-3) 0 0;
  padding: 0;
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
  box-shadow: var(--yfb-shadow-1);
  background-color: var(--yfb-surface-2);
  overflow: hidden;
}

.step-image--loaded {
  display: block;
}

.step-image__img {
  display: block;
  width: 100%;
  height: auto;
  max-width: 100%;
  object-fit: contain;
}
</style>
